// In-memory draft audio survives dashboard navigation; never persisted locally.
// No recording begins on import. One microphone session at a time.
let current = null;
const listeners = new Set();
const emit = () => listeners.forEach((listener) => listener());
const stopTracks = (stream) => stream?.getTracks().forEach((track) => track.stop());

export function subscribeRecorder(listener) { listeners.add(listener); return () => listeners.delete(listener); }
export function recorderDraft(ownerKey) { return current?.ownerKey === ownerKey ? current : null; }
export function discardRecorderDraft(ownerKey) {
  if (current?.ownerKey !== ownerKey || ["starting", "recording", "stopping"].includes(current.status)) return;
  current = null; emit();
}
export function importRecorderFile(ownerKey, file) {
  if (current && ["starting", "recording", "stopping"].includes(current.status)) throw new Error("Stop the current recording first.");
  current = { ownerKey, status: "captured", blob: file, filename: file.name,
    requestId: crypto.randomUUID(), bytes: file.size, error: "", startedAt: null };
  emit();
}
export async function startRecorder(ownerKey) {
  if (current && ["starting", "recording", "stopping"].includes(current.status)) throw new Error("A microphone recording is already active.");
  if (!window.isSecureContext || !navigator.mediaDevices?.getUserMedia || !window.MediaRecorder) {
    throw new Error("This browser cannot record audio here. Use a secure connection and a supported browser.");
  }
  const session = { ownerKey, status: "starting", blob: null, chunks: [], bytes: 0,
    requestId: crypto.randomUUID(), error: "", startedAt: null };
  current = session; emit();
  let stream;
  try {
    stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
    if (current !== session || session.status !== "starting") { stopTracks(stream); return; }
    const mime = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4"].find((type) => MediaRecorder.isTypeSupported(type));
    if (!mime) throw new Error("No supported recording format. Upload an existing audio file instead.");
    const recorder = new MediaRecorder(stream, { mimeType: mime, audioBitsPerSecond: 32000 });
    session.stream = stream; session.recorder = recorder;
    session.filename = mime.includes("mp4") ? "meeting.m4a" : "meeting.webm";
    recorder.ondataavailable = (event) => {
      if (event.data.size) { session.chunks.push(event.data); session.bytes += event.data.size; }
      if (session.bytes >= 23 * 1024 * 1024 && recorder.state === "recording") {
        session.error = "Recording stopped near the upload size limit. Save this part before continuing.";
        session.status = "stopping"; recorder.stop();
      }
      emit();
    };
    recorder.onerror = () => {
      session.error = "Recording was interrupted. Preserve the available audio before continuing.";
      if (recorder.state !== "inactive") recorder.stop();
      stopTracks(stream); emit();
    };
    recorder.onstop = () => {
      stopTracks(stream);
      session.blob = new Blob(session.chunks, { type: recorder.mimeType || mime });
      session.chunks = [];
      session.status = session.blob.size ? "captured" : "error";
      if (!session.blob.size) session.error = "No audio was captured.";
      emit();
    };
    recorder.start(1000); session.status = "recording"; session.startedAt = Date.now(); emit();
    stream.getAudioTracks().forEach((track) => track.addEventListener("ended", () => {
      if (recorder.state !== "inactive") {
        session.error = "Microphone access ended. Review the captured recording.";
        session.status = "stopping"; recorder.stop();
      }
    }));
  } catch (error) {
    stopTracks(stream); session.status = "error";
    session.error = error.name === "NotAllowedError" ? "Microphone permission was not granted." : error.message;
    emit(); throw error;
  }
}
export function stopRecorder(ownerKey) {
  const session = recorderDraft(ownerKey);
  if (!session) return;
  if (session.status === "starting") { session.status = "error"; session.error = "Recording cancelled."; emit(); return; }
  if (session.recorder?.state === "recording") {
    session.status = "stopping"; session.recorder.stop(); emit();
  }
}
if (typeof window !== "undefined") {
  window.addEventListener("beforeunload", (event) => {
    if (current && (["starting", "recording", "stopping"].includes(current.status) || current.blob?.size)) {
      event.preventDefault(); event.returnValue = "";
    }
  });
}
